'use client';
import { useEffect, useRef, useState } from 'react';

import { useHasImgLoaded } from '../../Hooks/UseHasImgLoaded';
import { SkeletonLoader } from '../Loading/SkeletonLoader';
import { generateSrcSet } from './GenerateSrcSet';
import { heycarLoader } from './HeycarLoader';

import styles from './HeycarImage.module.css';

type Props = {
  src: string;
  alt: string;
  sizes: string;
  className?: string;
  forceVisible?: boolean;
  loader?: ({ src, width }: { src: string; width: number }) => string;
  preload?: boolean;
  isInactive?: boolean;
  srcSetWidths?: number[];
  isStandAlone?: boolean;
};

export const HeycarImage = ({
  src,
  alt,
  sizes,
  className = '',
  forceVisible = false,
  loader = heycarLoader,
  preload = false,
  isInactive = false,
  srcSetWidths,
  isStandAlone,
  ...rest
}: Props) => {
  const [isInViewport, setIsInViewport] = useState<boolean | undefined>(forceVisible);
  const $imageRef = useRef<HTMLImageElement>(null);
  const $containerRef = useRef<HTMLDivElement>(null);
  //bypassing setting the images unloaded, because it slows down loading in the e2e env and causes tests to fail (mainly PDP)
  const bypassImgLoadedFalse = true;
  const { hasLoaded, imgOnLoad } = useHasImgLoaded($imageRef, bypassImgLoadedFalse);

  useEffect(() => {
    if (!$containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInViewport(true);
      },
      { root: null, rootMargin: '0px', threshold: 0 },
    );

    observer.observe($containerRef.current);

    return () => {
      if (!$containerRef.current) return;
      observer.unobserve($containerRef.current);
    };
  }, [$containerRef.current]);

  useEffect(() => {
    // cached images can fire onLoad before hydration attaches the handler
    const img = $imageRef.current;
    if (img?.complete && img.naturalHeight > 0 && $containerRef.current) {
      $containerRef.current.dataset.imgLoaded = 'true';
    }
  }, [isInViewport, src]);

  return (
    <>
      {!hasLoaded && isInViewport && <SkeletonLoader width={'100%'} height={'100%'} />}
      <div
        ref={$containerRef}
        className={`${styles.container} ${isStandAlone ? styles.isStandAlone : ''}`}
        data-has-loaded={hasLoaded}
        data-is-visible={isInViewport}
        key={src}
        {...rest}
      >
        {isInViewport && (
          <img
            className={`${className} ${styles.image}`}
            ref={$imageRef}
            src={src}
            srcSet={generateSrcSet(src, loader, srcSetWidths)}
            sizes={sizes}
            alt={alt}
            onLoad={() => {
              imgOnLoad();
              if ($containerRef.current) $containerRef.current.dataset.imgLoaded = 'true';
            }}
            onError={() => {
              // reveal the alt text instead of leaving the image transparent
              if ($containerRef.current) $containerRef.current.dataset.imgLoaded = 'true';
            }}
            decoding={preload ? 'auto' : 'async'}
            fetchPriority={preload ? 'high' : undefined}
            loading={preload ? 'eager' : 'lazy'}
            data-is-inactive={isInactive}
          />
        )}
      </div>
    </>
  );
};
